import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../App';
import { useSocket } from '../context/SocketContext';
import chatAPI from '../api/chatAPI';
import '../styles/ChatsPage.css';

const ChatsPage = () => { 
  const { user } = useAuth();
  const navigate = useNavigate();
  const { chatId } = useParams();
  const { socket, connected, joinChat, sendMessage, markMessagesAsRead } = useSocket();
  
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedChat, setSelectedChat] = useState(null);
  const [message, setMessage] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  
  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);
  
  // Load all chats
  useEffect(() => {
    const fetchChats = async () => {
      try {
        setLoading(true);
        const response = await chatAPI.getAllChats();
        setChats(response.data);
        
        // If there's a chatId in the URL, select that chat
        if (chatId) {
          const chat = response.data.find(c => c._id === chatId);
          if (chat) {
            // Instead of calling handleChatSelect which would join the chat twice,
            // we'll set up the chat directly here
            setSelectedChat({ ...chat, messages: [] });
            setSidebarOpen(false);
            
            // Fetch full chat details
            const chatResponse = await chatAPI.getChatById(chatId);
            setSelectedChat(chatResponse.data);
            
            // Join the chat room only once
            joinChat(chatId);
            
            // Mark messages as read if needed
            if (chat.unreadCount > 0) {
              await chatAPI.markMessagesAsRead(chatId);
              markMessagesAsRead(chatId);
              
              // Update the unread count in the chat list
              setChats(prevChats => 
                prevChats.map(c => 
                  c._id === chatId ? { ...c, unreadCount: 0 } : c
                )
              );
            }
          }
        }
      } catch (err) {
        setError('Failed to load chats. Please try again later.');
        console.error('Error loading chats:', err);
      } finally {
        setLoading(false);
      }
    };
    
    if (user) {
      fetchChats();
    }
  }, [user, chatId, joinChat, markMessagesAsRead]);
  
  // Socket event listeners
  useEffect(() => {
    if (!socket || !connected) return;
    
    // Listen for new messages
    const handleNewMessage = ({ chatId, message }) => {
      setChats(prevChats => {
        // Find the chat
        const updatedChats = [...prevChats];
        const chatIndex = updatedChats.findIndex(c => c._id === chatId);
        
        if (chatIndex !== -1) {
          // Update the chat with the new message
          const updatedChat = { ...updatedChats[chatIndex] };
          updatedChat.lastMessage = {
            content: message.content,
            sender: message.sender,
            createdAt: message.createdAt
          };
          
          // If this is the selected chat, mark as read
          if (selectedChat && selectedChat._id === chatId) {
            markMessagesAsRead(chatId);
          } else {
            // Otherwise increment unread count
            updatedChat.unreadCount = (updatedChat.unreadCount || 0) + 1;
          }
          
          // Move the chat to the top
          updatedChats.splice(chatIndex, 1);
          updatedChats.unshift(updatedChat);
          
          // If this is the selected chat, update the messages
          if (selectedChat && selectedChat._id === chatId) {
            setSelectedChat(prev => {
              if (!prev) return null;
              
              return {
                ...prev,
                messages: [...(prev.messages || []), message]
              };
            });
          }
        }
        
        return updatedChats;
      });
    };
    
    // Listen for messages read
    const handleMessagesRead = ({ chatId, userId }) => {
      // Only update if the other user read our messages
      if (userId !== user._id) {
        setSelectedChat(prev => {
          if (!prev || prev._id !== chatId) return prev;
          
          return {
            ...prev,
            messages: prev.messages.map(msg => ({
              ...msg,
              readBy: [...msg.readBy, userId]
            }))
          };
        });
      }
    };
    
    socket.on('new_message', handleNewMessage);
    socket.on('messages_read', handleMessagesRead);
    
    return () => {
      socket.off('new_message', handleNewMessage);
      socket.off('messages_read', handleMessagesRead);
    };
  }, [socket, connected, selectedChat, user, markMessagesAsRead]);
  
  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [selectedChat?.messages]);
  
  // Focus input when chat is selected
  useEffect(() => {
    if (selectedChat && messageInputRef.current) {
      messageInputRef.current.focus();
    }
  }, [selectedChat]);
  
  // Handle chat selection
  const handleChatSelect = async (chat) => {
    try {
      setSelectedChat({ ...chat, messages: [] });
      setSidebarOpen(false);
      
      // Update URL
      navigate(`/chats/${chat._id}`);
      
      // Fetch full chat details
      const response = await chatAPI.getChatById(chat._id);
      setSelectedChat(response.data);
      
      // Join the chat room
      joinChat(chat._id);
      
      // Mark messages as read
      if (chat.unreadCount > 0) {
        await chatAPI.markMessagesAsRead(chat._id);
        markMessagesAsRead(chat._id);
        
        // Update the unread count in the chat list
        setChats(prevChats => 
          prevChats.map(c => 
            c._id === chat._id ? { ...c, unreadCount: 0 } : c
          )
        );
      }
    } catch (err) {
      setError('Failed to load chat messages.');
      console.error('Error loading chat:', err);
    }
  };
  
  // Handle sending a message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!message.trim() || !selectedChat) return;
    
    try {
      // Send via API
      await chatAPI.sendMessage(selectedChat._id, message.trim());
      
      // Also send via socket for real-time
      sendMessage(selectedChat._id, message.trim());
      
      // Clear input
      setMessage('');
    } catch (err) {
      console.error('Error sending message:', err);
      // We don't set an error here to not disrupt the chat flow
    }
  };
  
  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { day: 'numeric', month: 'short' }) + ' ' +
        date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
  };
  
  // Get chat title
  const getChatTitle = (chat) => {
    if (!chat || !chat.otherParticipant) return 'Chat';
    return chat.otherParticipant.name || 'Chat';
  };
  
  // Get service info
  const getServiceInfo = (chat) => {
    if (!chat) return '';
    
    if (chat.booking) {
      return `${chat.booking.service} booking`;
    } else if (chat.subscription) {
      return `${chat.subscription.service} subscription`;
    }
    
    return '';
  };
  
  // Toggle sidebar
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };
  
  if (!user) {
    return <div className="chats-page">Please log in to view your chats.</div>;
  }
  
  return (
    <div className="chats-page">
      <div className={`chats-container ${sidebarOpen ? '' : 'sidebar-collapsed'}`}>
        {/* Sidebar with chat list */}
        <div className={`chats-sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
          <div className="sidebar-header">
            <h2>Chats</h2>
            <button className="close-sidebar-btn" onClick={toggleSidebar}>
              {sidebarOpen ? '×' : '≡'}
            </button>
          </div>
          
          {loading ? (
            <div className="chats-loading">Loading chats...</div>
          ) : error ? (
            <div className="chats-error">{error}</div>
          ) : chats.length === 0 ? (
            <div className="no-chats">
              <p>No chats yet.</p>
              <p>Chats will appear here after a booking or subscription is accepted.</p>
            </div>
          ) : (
            <div className="chats-list">
              {chats.map(chat => (
                <div 
                  key={chat._id} 
                  className={`chat-item ${selectedChat && selectedChat._id === chat._id ? 'selected' : ''}`}
                  onClick={() => handleChatSelect(chat)}
                >
                  <div className="chat-item-avatar">
                    {chat.otherParticipant?.name?.charAt(0) || '?'}
                    <span className={`status-indicator ${chat.otherParticipant?.isActive ? 'online' : 'offline'}`}></span>
                  </div>
                  <div className="chat-item-content">
                    <div className="chat-item-header">
                      <h3>{chat.otherParticipant?.name || 'Unknown'}</h3>
                      <span className="chat-time">{formatTime(chat.lastMessage?.createdAt)}</span>
                    </div>
                    <div className="chat-item-message">
                      {chat.lastMessage?.content || 'No messages yet'}
                      {chat.unreadCount > 0 && (
                        <span className="unread-badge">{chat.unreadCount}</span>
                      )}
                    </div>
                    <div className="chat-item-service">
                      {getServiceInfo(chat)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Chat window */}
        <div className="chat-window">
          {!selectedChat ? (
            <div className="no-chat-selected">
              <h2>Select a chat to start messaging</h2>
              {!sidebarOpen && (
                <button className="open-sidebar-btn" onClick={toggleSidebar}>
                  Open Chats
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Chat header */}
              <div className="chat-header">
                {!sidebarOpen && (
                  <button className="toggle-sidebar-btn" onClick={toggleSidebar}>
                    ≡
                  </button>
                )}
                <div className="chat-header-info">
                  <h2>{getChatTitle(selectedChat)}</h2>
                  <p>{getServiceInfo(selectedChat)}</p>
                </div>
              </div>
              
              {/* Messages area */}
              <div className="messages-container">
                {selectedChat.messages && selectedChat.messages.length > 0 ? (
                  <div className="messages-list">
                    {selectedChat.messages.map(msg => (
                      <div 
                        key={msg._id} 
                        className={`message ${msg.sender === user._id ? 'sent' : 'received'}`}
                      >
                        <div className="message-content">
                          {msg.content}
                          <span className="message-time">{formatTime(msg.createdAt)}</span>
                          {msg.sender === user._id && (
                            <span className="message-status">
                              {msg.readBy && msg.readBy.length > 1 ? 'Read' : 'Sent'}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                ) : (
                  <div className="no-messages">
                    <p>No messages yet. Start the conversation!</p>
                  </div>
                )}
              </div>
              
              {/* Message input */}
              <form className="message-input-container" onSubmit={handleSendMessage}>
                <input
                  type="text"
                  ref={messageInputRef}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Type a message..."
                  className="message-input"
                />
                <button 
                  type="submit" 
                  className="send-button"
                  disabled={!message.trim()}
                >
                  Send
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatsPage; 